import React, { useCallback } from 'react'
import { Box, Grid, Divider } from '@mui/material'
import { useIntl } from 'react-intl'

import { CustomTextInputDatepickerButton, TextInput, TextInputReplace } from '../text-input'
import { DateInput } from '../date-input'
import { SelectInput } from '../select-input'
import { NumberInput } from '../number-input'
import { RatingInput } from '../rating-input'

/**
 * @typedef {object} FormPanelFieldRef
 * @property {string} ref - Referencia al campo en fieldList.
 * @property {object} conf - Configuración de Grid para el campo (xs, sm, md, lg, xl).
 */

/**
 * @typedef {object} FormPanelBlock
 * @property {FormPanelFieldRef[][]} items - Array de filas, donde cada fila es un array de referencias a campos.
 * @property {function} [prepend] - Función que retorna un componente a renderizar antes del bloque. Recibe {data, combos, intl}.
 * @property {function} [append] - Función que retorna un componente a renderizar después del bloque. Recibe {data, combos, intl}.
 * @property {boolean} [foldable] - Si es false, el panel no será plegable (usado por FormFoldingPanel).
 * @property {boolean} [isFolded] - Estado inicial de plegado (usado por FormFoldingPanel).
 * @property {string} [title] - Título del bloque (usado por FormFoldingPanel).
 */

/**
 * @typedef {object} FormPanelFieldConfig
 * @property {function(object): object} fromData - Función que recibe data y retorna props adicionales para el campo.
 * @property {React.ComponentType} [component] - Componente personalizado a usar.
 * @property {'text' | 'read_only' | 'date' | 'custom_date' | 'select' | 'text_area' | 'number' | 'rating'} [componentType] - Tipo de componente a renderizar.
 * @property {string} name - Nombre del campo.
 * @property {boolean} [hidden] - Si es true, el campo no se renderiza.
 */

/**
 * @typedef {object} FormPanelProps
 * @property {object} data - Objeto con los datos del formulario.
 * @property {object} [combos] - Objeto con arrays de opciones para selects y otros campos.
 * @property {FormPanelBlock} block - Configuración del bloque de formulario a renderizar.
 * @property {Object.<string, FormPanelFieldConfig>} fieldList - Mapa de configuraciones de campos por referencia.
 * @property {Object.<string, boolean>} [errors={}] - Objeto con flags de error por nombre de campo.
 * @property {boolean} [showErrors] - Si es true, muestra los errores de validación.
 * @property {React.ReactNode} [extraContent] - Contenido adicional a renderizar al final del bloque.
 */

/**
 * Componente interno para renderizar un componente de campo individual.
 */
const Component = (props) => {
  const { component, defaultComponent, componentType, error, ...entryData } = props
  const ComponentClass = component || defaultComponent(componentType)
  return (
    <ComponentClass
      {...{
        ...entryData,
        error: error,
        helperText: error && entryData.text_invalid,
      }}
    />
  )
}

/**
 * Componente interno para renderizar una fila de campos en el formulario.
 */
const FormPanelBlockItem = ({ defaultComponent, data, errors, showErrors, fieldList, row, i }) => {
  return (
    <Grid container spacing={3} key={i} style={{ marginTop: 10, marginBottom: 1 }}>
      {row.length ? (
        row.map((entry) => {
          const { fromData, ...staticElement } = fieldList[entry.ref]
          const element = {
            ...staticElement,
            ...fromData(data),
          }
          if (element?.hidden) return null
          const { component, componentType, ...entryData } = element || {}
          const error = errors[entryData.name] && showErrors
          return (
            <Grid {...entry.conf} key={entry.ref}>
              <Component {...{ component, defaultComponent, componentType, error, ...entryData }} />
            </Grid>
          )
        })
      ) : (
        <Divider light style={{ width: '100%' }} />
      )}
    </Grid>
  )
}

/**
 * Componente interno para renderizar un bloque completo del formulario.
 */
const FormPanelBlock = ({
  defaultComponent,
  block,
  data,
  errors,
  showErrors,
  combos,
  fieldList,
  extraContent,
}) => {
  const intl = useIntl()
  return (
    <Box display="block" style={{ width: '100%' }}>
      {block.prepend ? block.prepend({ data, combos, intl }) : null}
      {block.items.map((row, i) => {
        return (
          <FormPanelBlockItem
            key={i}
            {...{ defaultComponent, data, errors, showErrors, fieldList, row, i }}
          />
        )
      })}
      {block.append ? block.append({ data, combos, intl }) : null}
      {extraContent}
    </Box>
  )
}

/**
 * Un componente de panel de formulario dinámico que renderiza campos basándose en configuración.
 *
 * Genera automáticamente un formulario basado en una configuración declarativa que especifica
 * qué campos mostrar, cómo organizarlos en filas y columnas, y qué tipo de input usar para cada uno.
 * Soporta múltiples tipos de campos (texto, fecha, select, número, etc.), validación de errores,
 * contenido personalizado antes/después de cada bloque, y componentes completamente personalizados.
 *
 * @component
 * @example
 * const MyDynamicForm = () => {
 *   const [formData, setFormData] = useState({ nombre: '', email: '' });
 *   const [errors, setErrors] = useState({});
 *
 *   const fieldList = {
 *     nombre: {
 *       fromData: (data) => ({
 *         title: 'Nombre',
 *         value: data.nombre,
 *         onChange: (e) => setFormData({...data, nombre: e.target.value}),
 *       }),
 *       componentType: 'text',
 *       name: 'nombre'
 *     },
 *     email: {
 *       fromData: (data) => ({
 *         title: 'Email',
 *         value: data.email,
 *         onChange: (e) => setFormData({...data, email: e.target.value}),
 *       }),
 *       componentType: 'text',
 *       name: 'email'
 *     }
 *   };
 *
 *   const block = {
 *     items: [
 *       [{ ref: 'nombre', conf: { xs: 12, md: 6 } }],
 *       [{ ref: 'email', conf: { xs: 12, md: 6 } }]
 *     ]
 *   };
 *
 *   return (
 *     <FormPanel
 *       data={formData}
 *       block={block}
 *       fieldList={fieldList}
 *       errors={errors}
 *       showErrors={true}
 *     />
 *   );
 * };
 *
 * @param {FormPanelProps} props - Las props del componente FormPanel.
 */
export const FormPanel = ({
  data,
  combos,
  block,
  fieldList,
  errors = {},
  showErrors,
  extraContent,
}) => {
  const defaultComponent = useCallback((type) => {
    switch (type) {
      case 'read_only':
        return TextInputReplace
      case 'date':
        return DateInput
      case 'custom_date':
        return CustomTextInputDatepickerButton
      case 'select':
        return SelectInput
      case 'text_area':
        return TextInput
      case 'number':
        return NumberInput
      case 'rating':
        return RatingInput
      default:
        return TextInput
    }
  }, [])

  return (
    <FormPanelBlock
      {...{
        defaultComponent,
        block,
        data,
        errors,
        showErrors,
        combos,
        fieldList,
        extraContent,
      }}
    />
  )
}

export default FormPanel
